import React from 'react';
import { PlatformLogo } from './PlatformIcons';
import { buildChatLink, ChatPlatform } from '../lib/chatLinks';

/**
 * "Copy & open <platform>" — one tap for the operator: the message goes on the
 * clipboard and the person's chat/profile opens (in the app if it's installed).
 * Then the operator just pastes, sends, comes back and presses Mark Sent.
 *
 * It is a real <a> link on purpose: phones treat links tapped by the user as
 * "open in app" far more reliably than script-driven navigation. Renders
 * nothing when the platform has no link (WhatsApp/SMS) or the username looks
 * wrong, so the existing Copy Contact / Copy Message buttons remain the way.
 */

const ACCENT: Record<ChatPlatform, string> = {
  Snapchat: '#FFFC00',
  Instagram: '#E1306C',
  Telegram: '#2AABEE',
  TikTok: '#25F4EE',
  SMS: '#CBD5E1',
};

// Snapchat and TikTok links land on the person's profile; the other two land
// straight in the conversation.
const CAPTION: Record<ChatPlatform, string> = {
  Snapchat: 'Opens their profile — tap Chat, then paste',
  Instagram: 'Opens the chat — just paste and send',
  Telegram: 'Opens the chat — just paste and send',
  TikTok: 'Opens their profile — tap Message, then paste',
  SMS: 'Opens Messages to this number — just paste and send',
};

// What the operator sees on the button (SMS opens the Messages app).
const LABEL = (p: ChatPlatform) => (p === 'SMS' ? 'Messages' : p);

/** Copies text. Uses the modern clipboard API and falls back to the old way. */
function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).then(() => true, () => false);
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return Promise.resolve(ok);
  } catch {
    return Promise.resolve(false);
  }
}

interface Props {
  platform?: string | null;
  contact?: string | null;
  /** The exact text to copy (same text the Copy Message button copies). */
  message: string;
  /** Called with a short status line — the Admin page shows it as a toast. */
  onNotify?: (text: string) => void;
  /** Spacing wrapper, e.g. "px-5 pb-3" or "mb-3". */
  className?: string;
}

export default function OpenChatButton({ platform, contact, message, onNotify, className = '' }: Props) {
  const href = buildChatLink(platform, contact);
  if (!href || !platform || !(platform in ACCENT)) return null;

  const p = platform as ChatPlatform;
  const accent = ACCENT[p];
  const label = LABEL(p);
  // https links open in a new tab; sms: links hand over to the Messages app.
  const isWeb = href.startsWith('http');

  // No preventDefault: the copy starts inside the tap, then the link opens as usual.
  const handleClick = () => {
    void copyToClipboard(message).then(ok =>
      onNotify?.(ok ? `Copied! Paste it in ${label}` : 'Copy failed — use Copy Message'),
    );
  };

  return (
    <div className={className}>
      <a
        href={href}
        {...(isWeb ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        onClick={handleClick}
        aria-label={`Copy the message and open ${label}`}
        className="w-full py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer hover:brightness-125 active:scale-[0.99]"
        style={{ background: `${accent}1f`, border: `1px solid ${accent}55`, color: accent }}
      >
        <PlatformLogo platform={p} size={20} />
        Copy &amp; open {label}
      </a>
      <p className="mt-1.5 text-[11px] text-zinc-500 text-center">{CAPTION[p]}</p>
    </div>
  );
}
